import * as THREE from "three";
import type { Game } from "../core/game";
import type { GhostName, GhostState } from "../core/types";
import { AstroActor, SlopActor, loadModels, type LoadedModels } from "./characters";
import { ISO_PITCH, ISO_YAW, TILE } from "./layout";
import { BurstField } from "./burst";
import { Minimap } from "./minimap";
import { PelletField, buildStation } from "./station";

/**
 * The isometric view.
 *
 * An orthographic camera on a fixed 45/35.264-degree bearing gives true
 * isometric projection; it tracks the astronaut rather than showing the whole
 * deck, so the station feels like somewhere you are inside of.
 */

// How many tiles fill the screen height. Tighter means the astronaut and the
// slop read as characters rather than specks; the minimap covers the loss of
// overview that comes with it.
const VIEW_TILES = 5;
// Mirrors TUNING.frightenedWarningSeconds; the glow quickens over this window.
const FRIGHTENED_WARNING_SECONDS = 2;

export interface SceneHandle {
  render(dt: number): void;
  /** Astronaut mixer state, exposed for the respawn regression test. */
  animationState(): { current: string; weights: Record<string, number>; facing: number };
  resize(): void;
  dispose(): void;
  readonly renderer: THREE.WebGLRenderer;
}

export const createScene = async (
  canvas: HTMLCanvasElement,
  game: Game,
  overlayHost: HTMLElement,
): Promise<SceneHandle> => {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.25;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x05070d);
  scene.fog = new THREE.Fog(0x05070d, VIEW_TILES * TILE * 0.9, VIEW_TILES * TILE * 2.1);

  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 400);

  // --- lighting -----------------------------------------------------------
  scene.add(new THREE.HemisphereLight(0x6f92bd, 0x101828, 0.9));
  const key = new THREE.DirectionalLight(0xdfe9ff, 2.1);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.camera.near = 1;
  key.shadow.camera.far = 90;
  const shadowSpan = VIEW_TILES * TILE * 0.8;
  key.shadow.camera.left = -shadowSpan;
  key.shadow.camera.right = shadowSpan;
  key.shadow.camera.top = shadowSpan;
  key.shadow.camera.bottom = -shadowSpan;
  key.shadow.bias = -0.0012;
  scene.add(key);
  scene.add(key.target);

  // A cool rim from the opposite side so hull edges read against the void. It
  // is deliberately weak and blue: a warm rim at this angle washes the whole
  // deck red, because the camera mostly sees floor.
  const rim = new THREE.DirectionalLight(0x6f86ff, 0.4);
  rim.position.set(-8, 6, -10);
  scene.add(rim);

  // --- static geometry ----------------------------------------------------
  const station = buildStation(game.maze);
  scene.add(station.group);

  const pellets = new PelletField();
  scene.add(pellets.group);
  const seedPellets = (): void => {
    const { storyPoints, quotaResets } = game.remainingPellets();
    pellets.sync(storyPoints, quotaResets);
  };
  seedPellets();
  let pelletsLeft = game.storyPointsLeft;

  // --- characters ---------------------------------------------------------
  const models: LoadedModels = await loadModels();
  const astro = new AstroActor(models);
  scene.add(astro.root);

  const slops = new Map<GhostName, SlopActor>();
  for (const ghost of game.ghosts) {
    const actor = new SlopActor(models, ghost.name);
    slops.set(ghost.name, actor);
    scene.add(actor.root);
  }

  // A lamp riding with the astronaut, so the corridor ahead is always readable.
  const lamp = new THREE.PointLight(0x9fd8ff, 22, TILE * 7, 2);
  lamp.position.y = 2.4;
  astro.root.add(lamp);

  const bursts = new BurstField();
  scene.add(bursts.group);

  const minimap = new Minimap(game.maze, overlayHost);
  const lastGhostState = new Map<GhostName, GhostState>();

  // Compile every shader before the first frame. WebGL compiles a material's
  // program the first time it is actually drawn, so without this the hitch
  // lands mid-play: the first catch burst, the first frightened tint, the
  // first time a slop turns to eyes. Doing it here moves that cost into load.
  await renderer.compileAsync(scene, camera);

  const focus = new THREE.Vector3();
  let lastStatus = game.status;

  const applyCamera = (): void => {
    const aspect = canvas.clientWidth / Math.max(canvas.clientHeight, 1);
    // Frame VIEW_TILES on whichever axis is SHORTER. Framing it on the
    // vertical alone, as this did, is fine on a 16:10 desktop and unplayable
    // in portrait: at a phone's 0.46 aspect the horizontal half-width collapses
    // to about one tile, and the player cannot see the junction they are
    // steering into. Dividing by the aspect when it drops below 1 holds the
    // width steady and spends the extra screen height on seeing further ahead.
    const half = (VIEW_TILES * TILE) / 2 / Math.min(aspect, 1);
    camera.left = -half * aspect;
    camera.right = half * aspect;
    camera.top = half;
    camera.bottom = -half;
    camera.updateProjectionMatrix();
  };

  const resize = (): void => {
    const width = canvas.clientWidth || window.innerWidth;
    const height = canvas.clientHeight || window.innerHeight;
    renderer.setSize(width, height, false);
    applyCamera();
  };
  resize();

  const render = (dt: number): void => {
    const snap = game.snapshot();

    // The astronaut leads the camera; lerping keeps the tunnel wrap from
    // whipping the view across the whole deck in one frame.
    const targetX = snap.player.x * TILE;
    const targetZ = snap.player.y * TILE;
    if (Math.abs(targetX - focus.x) > TILE * 6) focus.x = targetX;
    if (Math.abs(targetZ - focus.z) > TILE * 6) focus.z = targetZ;
    focus.x += (targetX - focus.x) * Math.min(1, dt * 6);
    focus.z += (targetZ - focus.z) * Math.min(1, dt * 6);

    const dist = VIEW_TILES * TILE;
    camera.position.set(
      focus.x + Math.cos(ISO_YAW) * Math.cos(ISO_PITCH) * dist,
      Math.sin(ISO_PITCH) * dist,
      focus.z + Math.sin(ISO_YAW) * Math.cos(ISO_PITCH) * dist,
    );
    camera.lookAt(focus.x, 0, focus.z);

    key.position.set(focus.x + 14, 26, focus.z + 10);
    key.target.position.set(focus.x, 0, focus.z);
    key.target.updateMatrixWorld();

    // Pellets only change when one is eaten, so rebuild on count change only.
    if (snap.storyPointsLeft !== pelletsLeft) {
      seedPellets();
      pelletsLeft = snap.storyPointsLeft;
    }
    pellets.update(dt);
    minimap.update(snap, game.remainingPellets());

    const moving = snap.status === "playing" && !snap.player.blocked;
    if (snap.status === "dying" && lastStatus !== "dying") astro.tumble();
    if (lastStatus === "dying" && snap.status !== "dying") astro.revive();
    if (snap.status === "levelComplete" && lastStatus !== "levelComplete") astro.celebrate();
    lastStatus = snap.status;
    astro.setPose(snap.player.x, snap.player.y, snap.player.dir, moving);
    // Urgency climbs from 0 to 1 over the last seconds of the Review, so the
    // pulse visibly quickens as the window closes.
    const urgency =
      snap.frightenedRemaining > 0
        ? 1 - Math.min(1, snap.frightenedRemaining / FRIGHTENED_WARNING_SECONDS)
        : 0;
    astro.setEmpowered(snap.mode === "frightened", urgency);
    astro.update(dt);

    for (const ghost of snap.ghosts) {
      const actor = slops.get(ghost.name);
      if (actor === undefined) continue;

      // Detect the catch from the state transition rather than the game's
      // event list: the simulation runs several fixed steps per rendered
      // frame, so events raised on an inner step would be gone by now.
      if (ghost.state === "eaten" && lastGhostState.get(ghost.name) !== "eaten") {
        bursts.fire(ghost.x, ghost.y);
        astro.chomp();
      }
      lastGhostState.set(ghost.name, ghost.state);

      actor.setPose(ghost.x, ghost.y, ghost.dir, {
        frightened: ghost.frightened,
        eaten: ghost.state === "eaten",
      });
      actor.update(dt, ghost.frightened ? 2.2 : 1);
    }
    bursts.update(dt);

    renderer.render(scene, camera);
  };

  return {
    render,
    resize,
    renderer,
    animationState: () => astro.animationState(),
    dispose: () => {
      station.dispose();
      pellets.dispose();
      minimap.dispose();
      bursts.dispose();
      renderer.dispose();
    },
  };
};
